(() => {
  const stickyEl = document.querySelector('[data-sa-sticky-atc]');
  if (!stickyEl) return;

  const productRoot =
    document.querySelector('product-info') ||
    document.querySelector('product-form') ||
    document.querySelector('[data-section-type="main-product"]') ||
    document.querySelector('.product');

  if (!productRoot) return;

  /* Parse variant JSON for instant label/price updates */
  let variantMap = {};
  const variantJSON = document.querySelector('[data-sticky-variants]');
  if (variantJSON) {
    try {
      const arr = JSON.parse('[' + variantJSON.textContent + ']');
      arr.forEach((v) => { variantMap[v.id] = v; });
    } catch (_) { /* graceful degrade */ }
  }

  const whenIdle = (fn, timeout = 1200) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fn, { timeout });
    } else {
      setTimeout(fn, 300);
    }
  };

  const StickyATC = {
    config: {
      productRoot,
      form: null,
      stickyATC: stickyEl,
    },
    visibilityObserver: null,
    visibilityRafId: null,
    visibilityHandler: null,
    viewportHandler: null,

    selectors: {
      form: 'product-form, form[data-product-form], .product-form',
      variantSelects: 'variant-selects, [data-variant-selects]',
      mainAtcButton: '.product-form__submit[name="add"], button[type="submit"][name="add"], .product-form__submit',
      stickyAtcButton: '[data-sticky-atc-btn]',
      priceText: '[data-sticky-current-price]',
      variantText: '[data-sticky-variant]',
      titleText: '[data-sticky-title]',
    },

    init() {
      this.config.form = productRoot.querySelector(this.selectors.form);
      if (!this.config.form || !this.config.stickyATC) return;

      this.initStickyATC();
      this.bindVariantChangeReinit();
    },

    initStickyATC() {
      if (!this.config.stickyATC || !this.config.form) return;

      this.mountStickyToBody();

      if (window.initOnce && !window.initOnce(this.config.stickyATC, 'sticky-atc')) {
        return;
      }

      const atcButton = this.config.stickyATC.querySelector(this.selectors.stickyAtcButton);
      if (!atcButton) return;

      atcButton.addEventListener('click', (event) => {
        event.preventDefault();
        const submitButton = this.config.form.querySelector(this.selectors.mainAtcButton);
        if (submitButton) {
          submitButton.click();
        }
      });

      this.updateStickyATCState();
      this.bindVisibilityObservers();
    },

    mountStickyToBody() {
      if (!this.config.stickyATC || !document.body) return;
      if (this.config.stickyATC.parentElement !== document.body) {
        document.body.appendChild(this.config.stickyATC);
      }
      this.syncViewportPinnedBottom();
    },

    setStickyVisible(visible) {
      if (!this.config.stickyATC) return;
      this.config.stickyATC.classList.toggle('is-visible', visible);
      this.config.stickyATC.setAttribute('aria-hidden', visible ? 'false' : 'true');
    },

    scheduleVisibilityUpdate() {
      if (this.visibilityRafId) return;
      this.visibilityRafId = requestAnimationFrame(() => {
        this.visibilityRafId = null;
        this.updateStickyVisibility();
      });
    },

    syncViewportPinnedBottom() {
      if (!this.config.stickyATC) return;

      let bottomOffset = 0;
      const vv = window.visualViewport;

      if (vv) {
        const layoutHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        bottomOffset = Math.max(0, layoutHeight - vv.height - vv.offsetTop);
      }

      this.config.stickyATC.style.setProperty('--sa-sticky-atc-bottom', `${Math.round(bottomOffset)}px`);
    },

    isMainAtcButtonVisible() {
      if (!this.config.form) return false;
      const mainAtcButton = this.config.form.querySelector(this.selectors.mainAtcButton);
      if (!mainAtcButton) return false;

      const styles = window.getComputedStyle(mainAtcButton);
      if (styles.display === 'none' || styles.visibility === 'hidden') return false;

      const rect = mainAtcButton.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

      return rect.bottom > 0 && rect.right > 0 && rect.top < viewportHeight && rect.left < viewportWidth;
    },

    updateStickyVisibility() {
      const mainVisible = this.isMainAtcButtonVisible();
      this.setStickyVisible(!mainVisible);
    },

    bindVisibilityObservers() {
      if (!this.config.stickyATC || !this.config.form) return;
      if (this.config.stickyATC.hasAttribute('data-sticky-visibility-bound')) {
        this.scheduleVisibilityUpdate();
        return;
      }

      this.config.stickyATC.setAttribute('data-sticky-visibility-bound', 'true');
      this.setStickyVisible(false);

      const mainAtcButton = this.config.form.querySelector(this.selectors.mainAtcButton);
      if (mainAtcButton && 'IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(
          () => this.scheduleVisibilityUpdate(),
          { threshold: [0, 0.01, 0.1, 0.5, 1] }
        );
        this.visibilityObserver.observe(mainAtcButton);
      }

      this.visibilityHandler = () => this.scheduleVisibilityUpdate();
      this.viewportHandler = () => {
        this.syncViewportPinnedBottom();
        this.scheduleVisibilityUpdate();
      };
      window.addEventListener('scroll', this.visibilityHandler, { passive: true });
      window.addEventListener('resize', this.visibilityHandler);
      window.addEventListener('orientationchange', this.visibilityHandler);
      document.addEventListener('visibilitychange', this.visibilityHandler);

      window.addEventListener('scroll', this.viewportHandler, { passive: true });
      window.addEventListener('resize', this.viewportHandler);
      window.addEventListener('orientationchange', this.viewportHandler);

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.viewportHandler);
        window.visualViewport.addEventListener('scroll', this.viewportHandler);
      }

      this.syncViewportPinnedBottom();
      this.scheduleVisibilityUpdate();
    },

    bindVariantChangeReinit() {
      if (!this.config.stickyATC || !this.config.form) return;

      if (this.config.form.hasAttribute('data-variant-bound')) return;
      this.config.form.setAttribute('data-variant-bound', 'true');

      const mainButton = this.config.form.querySelector(this.selectors.mainAtcButton);
      if (mainButton && 'MutationObserver' in window) {
        const buttonObserver = new MutationObserver(() => {
          this.updateStickyATCState();
        });

        buttonObserver.observe(mainButton, {
          attributes: true,
          attributeFilter: ['disabled'],
          characterData: true,
          subtree: true,
        });
      }

      if (window.subscribe && window.PUB_SUB_EVENTS?.optionValueSelectionChange) {
        window.subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
          setTimeout(() => {
            this.updateStickyATCState();
            this.scheduleVisibilityUpdate();
          }, 50);
        });
      } else {
        const variantSelects = this.config.form.querySelectorAll('select, [data-option-value]');
        variantSelects.forEach((select) => {
          select.addEventListener('change', () => {
            setTimeout(() => {
              this.updateStickyATCState();
              this.scheduleVisibilityUpdate();
            }, 50);
          });
        });
      }

      const variantInput = this.config.form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.addEventListener('change', () => {
          setTimeout(() => {
            this.updateStickyATCState();
            this.scheduleVisibilityUpdate();
          }, 50);
        });
      }
    },

    isVariantSelected() {
      const variantInput = this.config.form?.querySelector('input[name="id"]');
      if (!variantInput) return false;
      const variantId = variantInput.value;
      return !!variantId && variantId !== '' && variantId !== '0';
    },

    getSelectedVariantId() {
      const variantInput = this.config.form?.querySelector('input[name="id"]');
      return variantInput ? variantInput.value : null;
    },

    scrollToVariantSelector() {
      const variantSection =
        this.config.form?.querySelector(this.selectors.variantSelects) ||
        this.config.form?.querySelector('select[name*="option"]') ||
        this.config.form;

      if (variantSection) {
        variantSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    updateStickyATCState() {
      if (!this.config.stickyATC || !this.config.form) return;

      const atcButton = this.config.stickyATC.querySelector(this.selectors.stickyAtcButton);
      const priceEl = this.config.stickyATC.querySelector(this.selectors.priceText);
      const variantEl = this.config.stickyATC.querySelector(this.selectors.variantText);
      const titleEl = this.config.stickyATC.querySelector(this.selectors.titleText);
      const mainButton = this.config.form.querySelector(this.selectors.mainAtcButton);

      if (!mainButton || !atcButton) return;

      const hasVariant = this.isVariantSelected();
      const variantId = this.getSelectedVariantId();
      const variantData = variantMap[variantId];

      if (!hasVariant) {
        atcButton.setAttribute('disabled', 'disabled');
        atcButton.setAttribute('data-state', 'no-variant');
        atcButton.textContent = 'Select Variant';

        if (titleEl) titleEl.classList.add('is-hidden');

        const newButton = atcButton.cloneNode(true);
        atcButton.parentNode.replaceChild(newButton, atcButton);

        newButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollToVariantSelector();
        });

        this.scheduleVisibilityUpdate();

        return;
      }

      if (titleEl) titleEl.classList.remove('is-hidden');

      /* Update variant label */
      if (variantEl && variantData) {
        if (variantData.label) {
          variantEl.textContent = variantData.label;
          variantEl.style.display = '';
        } else {
          variantEl.style.display = 'none';
        }
      }

      /* Update price from variant map (instant, no DOM scraping) */
      if (priceEl && variantData) {
        priceEl.textContent = variantData.price;
      }

      /* Update button state */
      if (mainButton.hasAttribute('disabled')) {
        atcButton.setAttribute('disabled', 'disabled');
        atcButton.setAttribute('data-state', 'sold-out');
        atcButton.textContent = mainButton.textContent || 'Sold Out';
      } else {
        atcButton.removeAttribute('disabled');
        atcButton.setAttribute('data-state', 'available');
        atcButton.textContent = mainButton.textContent || 'Add to Cart';
      }

      this.scheduleVisibilityUpdate();
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => whenIdle(() => StickyATC.init()));
  } else {
    whenIdle(() => StickyATC.init());
  }
})();
